import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createRental, getBikes, getPlans, getCurrentShift, createShift, incrementShiftTotals } from "@/integrations/supabase/services";
import { Customer, Plan } from "@/types/models";

const rentalFormSchema = z.object({
  customerId: z.string().min(1, "La cédula es requerida"),
  customerName: z.string().min(1, "El nombre es requerido"),
  customerPhone: z.string().min(1, "El teléfono es requerido"),
  bikeIds: z.array(z.string()).min(1, "Debe seleccionar al menos una bicicleta"),
  planId: z.string().min(1, "Debe seleccionar un plan"),
  durationHours: z.number().min(0.1, "La duración debe ser mayor a 0"),
});

export type RentalFormValues = z.infer<typeof rentalFormSchema>;

export const useRentalForm = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBikes, setSelectedBikes] = useState<string[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [durationHours, setDurationHours] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const form = useForm<RentalFormValues>({
    resolver: zodResolver(rentalFormSchema),
    defaultValues: {
      customerId: "",
      customerName: "",
      customerPhone: "",
      bikeIds: [],
      planId: "",
      durationHours: 1,
    },
  });

  // Update form when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      form.setValue("customerId", selectedCustomer.id_card.toString());
      form.setValue("customerName", selectedCustomer.name);
      form.setValue("customerPhone", selectedCustomer.phone);
    } else {
      form.setValue("customerId", "");
      form.setValue("customerName", "");
      form.setValue("customerPhone", "");
    }
  }, [selectedCustomer, form]);

  // Fetch bikes for the current user's branch
  const { data: bikes = [] } = useQuery({
    queryKey: ['bikes', profile?.branch_id],
    queryFn: async () => {
      const allBikes = await getBikes();
      return profile?.branch_id 
        ? allBikes.filter(bike => bike.branch_id === profile.branch_id && bike.active)
        : allBikes.filter(bike => bike.active);
    },
    enabled: !!profile,
  });

  // Fetch plans
  const { data: plans = [] } = useQuery({
    queryKey: ['plans', profile?.branch_id],
    queryFn: () => getPlans(profile?.branch_id),
  });

  // Get selected plan details
  const selectedPlanData = plans.find(plan => plan.id === selectedPlan);

  // Get selected bikes data
  const selectedBikesData = bikes.filter(bike => selectedBikes.includes(bike.id));

  const getPlanDurationInHours = (plan: Plan) => {
    let planDurationInHours = 1; // Default to 1 if parsing fails or plan is undefined
    if (plan && plan.time_duration) { // Add null check for plan.time_duration
      if (plan.time_duration.includes('min')) {
        const minutes = parseInt(plan.time_duration);
        planDurationInHours = minutes / 60;
      } else if (plan.time_duration.includes('hora')) {
        planDurationInHours = parseInt(plan.time_duration);
      }
    }
    return planDurationInHours;
  }

  // Calculate total amount based on plan duration and actual duration
  const totalAmount = selectedPlanData ? (() => {
    // Convert plan duration to hours for calculation
    let planDurationInHours = getPlanDurationInHours(selectedPlanData);
    
    // Calculate rate per hour based on plan
    const ratePerHour = selectedPlanData.price / planDurationInHours;
    
    // Total = rate per hour * duration * number of bikes
    return ratePerHour * durationHours * selectedBikes.length;
  })() : 0;

  // Calculate expected return time
  const expectedReturnTime = new Date(Date.now() + durationHours * 60 * 60 * 1000);

  // Create rental mutation
  const createRentalMutation = useMutation({
    mutationFn: async (data: RentalFormValues) => {
      if (!profile?.id || !profile?.branch_id) {
        throw new Error("Perfil de usuario incompleto");
      }

      if (!selectedCustomer) {
        throw new Error("Debe seleccionar un cliente");
      }

      let currentShift = await getCurrentShift(profile.id);
      
      if (!currentShift) {
        currentShift = await createShift(profile.id);
        queryClient.invalidateQueries({ queryKey: ['shifts'] });
      } else if (currentShift.end_time) {
        throw new Error("La jornada actual ya ha finalizado");
      }

      // Create rentals for each selected bike
      const rentalPromises = data.bikeIds.map(bikeId => 
        createRental({
          customer_id: selectedCustomer.id,
          bike_id: bikeId,
          manager_id: profile.id,
          branch_id: profile.branch_id,
          plan_id: data.planId,
          start_date: new Date(),
          end_date: null,
          expected_return_time: expectedReturnTime,
          duration_hours: data.durationHours,
          total_amount: totalAmount / data.bikeIds.length,
          status: 'active'
        })
      );

      await Promise.all(rentalPromises);

      // Atomically update shift totals
      await incrementShiftTotals(currentShift.id, totalAmount, data.bikeIds.length);
    },
    onSuccess: () => {
      toast({
        title: "Alquiler registrado",
        description: "El alquiler se ha registrado exitosamente",
      });
      
      // Invalidate bikes query to refresh availability
      queryClient.invalidateQueries({ queryKey: ['bikes'] });
      queryClient.invalidateQueries({ queryKey: ['rentals'] });
      queryClient.invalidateQueries({ queryKey: ['active-rentals'] });
      
      navigate("/rentals-operator");
    },
    onError: (error: any) => {
      console.error('Error creating rental:', error);
      toast({
        variant: "destructive",
        title: "Error al registrar alquiler",
        description: error.message || "Ocurrió un error inesperado",
      });
    },
  });

  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      setSearchTerm(customer.id_card.toString());
    }
  };

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    if (!search) {
      setSelectedCustomer(null);
    }
  };

  const handleBikesChange = (bikeIds: string[]) => {
    setSelectedBikes(bikeIds);
    form.setValue("bikeIds", bikeIds);
  };

  const handlePlanChange = (planId: string) => {
    setSelectedPlan(planId);
    form.setValue("planId", planId);
    const planData = plans.find(p => p.id === planId);

    if (planData && !planData.extend) {
      const planDuration = getPlanDurationInHours(planData);
      handleDurationChange(planDuration);
    } else {
      handleDurationChange(1); // Reset to 1 for extendable plans
    }
  };

  const handleDurationChange = (duration: number) => {
    setDurationHours(duration);    
    form.setValue("durationHours", duration);
  };

  const onSubmit = async (data: RentalFormValues) => {
    setIsLoading(true);
    
    try {
      await createRentalMutation.mutateAsync(data);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    selectedCustomer,
    searchTerm,
    selectedBikes: selectedBikesData,
    selectedPlan: selectedPlanData,
    bikes,
    plans,
    durationHours,
    totalAmount,
    expectedReturnTime,
    isLoading: isLoading || createRentalMutation.isPending,
    profile,
    handleCustomerSelect,
    handleSearchChange,
    handleBikesChange,
    handlePlanChange,
    handleDurationChange,
    onSubmit
  };
};
